import { FeatureLike } from 'ol/Feature';
import { LineString, MultiLineString, Point } from 'ol/geom';
import { Fill, Icon, Stroke, Style } from 'ol/style';
import { StyleFunction } from 'ol/style/Style';
import { ArcGISRenderer, ArcGISSymbol, CIMSymbol, CIMSymbolAnimation, CIMSymbolLayer } from '../models/arcgis-types';
import { ESRI_INCHES_PER_METER } from '../models/gis-service-list';
import { getCIMAnimationScale, isPlayingCIMAnimation, isSupportedCIMAnimation } from './arcgis-cim-animation.util';
import { getArcGISRendererSymbols, normalizeArcGISRenderer } from './arcgis-renderer.util';

const CSS_PIXELS_PER_INCH = 96;
const CSS_PIXELS_PER_POINT = CSS_PIXELS_PER_INCH / 72;

export interface ArcGISCompiledStyle {
    style: StyleFunction;
    animated: boolean;
}

export interface CIMLegend {
    geometry: 'point' | 'line' | 'polygon';
    url: string | null;
    color: string | null;
    borderColor: string | null;
    height?: string;
}

interface MarkerImage {
    markup: string;
    url: string;
    width: number;
    height: number;
    anchor: number[];
    scale: number;
    rotation: number;
}

interface SymbolPart {
    layer: CIMSymbolLayer;
    style: Style;
    marker?: MarkerImage;
    animation?: CIMSymbolAnimation;
}

function getSymbol(symbol: ArcGISSymbol | null | undefined): CIMSymbol | null {
    const reference = symbol?.data?.type === 'CIMSymbolReference' ? symbol.data : symbol;
    return reference?.type === 'CIMSymbolReference' ? reference.symbol ?? null : null;
}

export function isCIMSymbol(symbol: ArcGISSymbol | null | undefined): boolean {
    return symbol?.type === 'CIMSymbolReference' || symbol?.data?.type === 'CIMSymbolReference';
}

function colorToRGBA(color: number[] | undefined): string {
    const channel = (value: number | undefined, fallback: number): number =>
        value !== undefined && Number.isFinite(value) ? Math.max(0, Math.min(255, value)) : fallback;
    return `rgba(${channel(color?.[0], 0)}, ${channel(color?.[1], 0)}, ${channel(color?.[2], 0)}, ${channel(color?.[3], 255) / 255})`;
}

function getLineCap(value: string | undefined): CanvasLineCap {
    switch (value?.toLowerCase()) {
        case 'butt': return 'butt';
        case 'square': return 'square';
        default: return 'round';
    }
}

function getLineJoin(value: string | undefined): CanvasLineJoin {
    switch (value?.toLowerCase()) {
        case 'bevel': return 'bevel';
        case 'miter': return 'miter';
        default: return 'round';
    }
}

function offsetRing(ring: number[][], distance: number): number[][] {
    const points = ring.slice(0, -1);
    if (points.length < 3 || ring[0][0] !== ring[ring.length - 1][0] || ring[0][1] !== ring[ring.length - 1][1]) {
        throw new Error('Unsupported open CIM marker offset');
    }
    const result = points.map((point, index) => {
        const previous = points[(index + points.length - 1) % points.length];
        const next = points[(index + 1) % points.length];
        const before = [point[0] - previous[0], point[1] - previous[1]];
        const after = [next[0] - point[0], next[1] - point[1]];
        const beforeLength = Math.hypot(...before);
        const afterLength = Math.hypot(...after);
        if (!beforeLength || !afterLength) throw new Error('Invalid CIM offset geometry');
        const normalBefore = [-before[1] / beforeLength, before[0] / beforeLength];
        const normalAfter = [-after[1] / afterLength, after[0] / afterLength];
        const denominator = 1 + normalBefore[0] * normalAfter[0] + normalBefore[1] * normalAfter[1];
        if (denominator < 0.001) throw new Error('Unsupported CIM offset corner');
        return [
            point[0] + distance * (normalBefore[0] + normalAfter[0]) / denominator,
            point[1] + distance * (normalBefore[1] + normalAfter[1]) / denominator
        ];
    });
    return result.concat([result[0]]);
}

function createMarkerImage(layer: CIMSymbolLayer): MarkerImage {
    const frame = layer.frame;
    if (!frame || ![frame.xmin, frame.ymin, frame.xmax, frame.ymax].every(Number.isFinite) ||
        frame.xmax <= frame.xmin || frame.ymax <= frame.ymin) {
        throw new Error('Invalid CIM marker frame');
    }
    const paths: string[] = [];
    let padding = 0;
    for (const graphic of layer.markerGraphics || []) {
        const rings = graphic.geometry?.rings;
        if (!rings?.length || !rings.every(ring => ring.length >= 3 && ring.every(point => point.length >= 2 && point.every(Number.isFinite))) ||
            graphic.symbol?.type !== 'CIMPolygonSymbol' || graphic.symbol.effects?.length) {
            throw new Error('Unsupported CIM marker graphic');
        }
        for (const part of (graphic.symbol.symbolLayers || []).slice().reverse()) {
            if (part.enable === false) continue;
            if (part.type !== 'CIMSolidFill' && part.type !== 'CIMSolidStroke') throw new Error('Unsupported CIM marker paint');
            let paintedRings = rings;
            for (const effect of part.effects || []) {
                if (effect.type !== 'CIMGeometricEffectOffset' || effect.method !== 'Square' || !Number.isFinite(effect.offset)) {
                    throw new Error('Unsupported CIM marker effect');
                }
                const offset = effect.offset ?? 0;
                paintedRings = paintedRings.map(ring => offsetRing(ring, offset));
                padding = Math.max(padding, Math.abs(offset));
            }
            const path = paintedRings.map(ring => ring.map((point, index) =>
                `${index ? 'L' : 'M'}${point[0]} ${frame.ymin + frame.ymax - point[1]}`
            ).join(' ') + ' Z').join(' ');
            const color = colorToRGBA(part.color);
            if (part.type === 'CIMSolidFill') {
                paths.push(`<path d="${path}" fill="${color}" fill-rule="evenodd"/>`);
            } else if ((part.width ?? 1) > 0) {
                const width = part.width ?? 1;
                padding = Math.max(padding, width * (part.miterLimit ?? 4) / 2);
                paths.push(`<path d="${path}" fill="none" stroke="${color}" stroke-width="${width}" stroke-linejoin="${getLineJoin(part.joinStyle)}" stroke-linecap="${getLineCap(part.capStyle)}"/>`);
            }
        }
    }
    if (!paths.length) throw new Error('Empty CIM marker graphic');
    const width = frame.xmax - frame.xmin + padding * 2;
    const height = frame.ymax - frame.ymin + padding * 2;
    const size = layer.size ?? 10;
    const scale = size * CSS_PIXELS_PER_POINT / (frame.ymax - frame.ymin);
    const x = layer.anchorPoint?.x ?? 0;
    const y = layer.anchorPoint?.y ?? 0;
    const anchorScaleX = layer.anchorPointUnits === 'Absolute' ? CSS_PIXELS_PER_POINT / (scale || 1) : frame.xmax - frame.xmin;
    const anchorScaleY = layer.anchorPointUnits === 'Absolute' ? CSS_PIXELS_PER_POINT / (scale || 1) : frame.ymax - frame.ymin;
    const markup = `<g transform="translate(${-frame.xmin + padding} ${-frame.ymin + padding})">${paths.join('')}</g>`;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">${markup}</svg>`;
    return {
        markup, url: 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg), width, height,
        anchor: [0.5 + x * anchorScaleX / width, 0.5 - y * anchorScaleY / height], scale,
        rotation: (layer.rotation ?? 0) * (layer.rotateClockwise ? 1 : -1) * Math.PI / 180
    };
}

function getSizeScale(renderer: ArcGISRenderer | undefined, baseWidth: number, resolution: number): number {
    const size = renderer?.visualVariables?.find(variable => variable.type === 'sizeInfo' && variable.valueExpression === '$view.scale');
    const stops = size?.stops?.filter(stop => typeof stop.size === 'number' && Number.isFinite(stop.size) && Number.isFinite(stop.value))
        .slice().sort((left, right) => left.value - right.value);
    if (!stops?.length || baseWidth <= 0) return 1;
    const scale = resolution * ESRI_INCHES_PER_METER * CSS_PIXELS_PER_INCH;
    const upperIndex = stops.findIndex(stop => stop.value >= scale);
    if (upperIndex === 0) return (stops[0].size ?? baseWidth) / baseWidth;
    if (upperIndex === -1) return (stops[stops.length - 1].size ?? baseWidth) / baseWidth;
    const lower = stops[upperIndex - 1];
    const upper = stops[upperIndex];
    const ratio = (scale - lower.value) / (upper.value - lower.value);
    return ((lower.size ?? baseWidth) + ratio * ((upper.size ?? baseWidth) - (lower.size ?? baseWidth))) / baseWidth;
}

function compileParts(symbol: CIMSymbol): SymbolPart[] {
    if (!['CIMPointSymbol', 'CIMLineSymbol', 'CIMPolygonSymbol'].includes(symbol.type) || symbol.effects?.length) {
        throw new Error('Unsupported CIM symbol');
    }
    return (symbol.symbolLayers || []).filter(layer => layer.enable !== false).slice().reverse().map((layer, index) => {
        if (layer.effects?.length) throw new Error('Unsupported CIM symbol-layer effect');
        const animations = layer.animations || [];
        const supported = !animations.length || (layer.type === 'CIMVectorMarker' && animations.every(isSupportedCIMAnimation));
        const animation = supported ? animations.find(isPlayingCIMAnimation) : undefined;
        const style = new Style({ zIndex: index });
        if (symbol.type !== 'CIMPointSymbol' && layer.type === 'CIMSolidStroke') {
            if ((layer.width ?? 1) > 0) {
                style.setStroke(new Stroke({ color: colorToRGBA(layer.color), width: (layer.width ?? 1) * CSS_PIXELS_PER_POINT,
                    lineCap: getLineCap(layer.capStyle), lineJoin: getLineJoin(layer.joinStyle), miterLimit: layer.miterLimit ?? 4 }));
            }
            return { layer, style, animation };
        }
        if (symbol.type === 'CIMPolygonSymbol' && layer.type === 'CIMSolidFill') {
            style.setFill(new Fill({ color: colorToRGBA(layer.color) }));
            return { layer, style, animation };
        }
        if (layer.type === 'CIMVectorMarker' && (symbol.type === 'CIMPointSymbol' ||
            (symbol.type === 'CIMLineSymbol' && layer.markerPlacement?.type === 'CIMMarkerPlacementOnLine' && layer.markerPlacement.relativeTo === 'LineMiddle'))) {
            const marker = createMarkerImage(layer);
            const angle = (symbol.angle ?? 0) * Math.PI / 180;
            marker.rotation -= angle;
            const x = (layer.offsetX ?? 0) * CSS_PIXELS_PER_POINT;
            const y = (layer.offsetY ?? 0) * CSS_PIXELS_PER_POINT;
            const displacement = [x * Math.cos(angle) - y * Math.sin(angle), x * Math.sin(angle) + y * Math.cos(angle)];
            const cosine = Math.cos(marker.rotation);
            const sine = Math.sin(marker.rotation);
            const scale = marker.scale || 1;
            marker.anchor[0] -= (cosine * displacement[0] - sine * displacement[1]) / (scale * marker.width);
            marker.anchor[1] += (sine * displacement[0] + cosine * displacement[1]) / (scale * marker.height);
            style.setImage(new Icon({ src: marker.url, imgSize: [marker.width, marker.height], anchor: marker.anchor,
                scale: marker.scale, rotation: marker.rotation, rotateWithView: symbol.angleAlignment === 'Map' }));
            return { layer, style, marker, animation };
        }
        throw new Error(`Unsupported CIM symbol layer: ${layer.type}`);
    });
}

export function createCIMSymbolStyle(
    symbol: ArcGISSymbol | undefined,
    getTime: () => number = () => 0,
    renderer?: ArcGISRenderer,
): ArcGISCompiledStyle | null {
    const cim = getSymbol(symbol);
    if (!cim && isCIMSymbol(symbol)) throw new Error('Invalid CIM symbol reference');
    if (!cim) return null;
    const parts = compileParts(cim);
    const baseSize = cim.type === 'CIMPointSymbol'
        ? Math.max(0, ...parts.map(part => part.layer.size ?? 10))
        : parts.find(part => part.layer.type === 'CIMSolidStroke')?.layer.width ?? 1;
    return {
        animated: parts.some(part => !!part.animation),
        style: (feature: FeatureLike, resolution: number): Style[] => {
            const sizeScale = getSizeScale(renderer, baseSize, resolution);
            const styles: Style[] = [];
            for (const part of parts) {
                const scale = sizeScale * getCIMAnimationScale(part.animation, getTime());
                const stroke = part.style.getStroke();
                if (stroke) stroke.setWidth((part.layer.width ?? 1) * scale * CSS_PIXELS_PER_POINT);
                const image = part.style.getImage();
                if (part.marker && image) image.setScale(part.marker.scale * scale);
                if (part.marker && cim.type === 'CIMLineSymbol') {
                    const geometry = feature.getGeometry();
                    const lines = geometry instanceof MultiLineString ? geometry.getLineStrings() : geometry instanceof LineString ? [geometry] : [];
                    for (const line of lines) {
                        if (!line.getLength()) continue;
                        const before = line.getCoordinateAt(0.4999);
                        const after = line.getCoordinateAt(0.5001);
                        const style = part.style.clone();
                        style.setGeometry(new Point(line.getCoordinateAt(0.5)));
                        const markerImage = style.getImage();
                        if (markerImage) {
                            markerImage.setRotation(part.marker.rotation - (part.layer.markerPlacement?.angleToLine ? Math.atan2(after[1] - before[1], after[0] - before[0]) : 0));
                            markerImage.setRotateWithView(true);
                        }
                        styles.push(style);
                    }
                } else {
                    styles.push(part.style);
                }
            }
            return styles;
        }
    };
}

export function getCIMRendererError(renderer: ArcGISRenderer): string | null {
    try {
        for (const symbol of getArcGISRendererSymbols(normalizeArcGISRenderer(renderer))) {
            const cim = getSymbol(symbol);
            if (!cim && isCIMSymbol(symbol)) throw new Error('Invalid CIM symbol reference');
            if (cim) compileParts(cim);
        }
        return null;
    } catch (error) {
        return error instanceof Error ? error.message : String(error);
    }
}

export function createCIMStyleFunction(renderer: ArcGISRenderer | null | undefined, getTime: () => number = () => 0): ArcGISCompiledStyle | null {
    return renderer?.type === 'simple' ? createCIMSymbolStyle(renderer.symbol, getTime, renderer) : null;
}

export function getCIMLegend(symbol: ArcGISSymbol | null | undefined): CIMLegend | null {
    const cim = getSymbol(symbol);
    if (!cim) return null;
    try {
        const parts = compileParts(cim);
        if (cim.type === 'CIMPointSymbol') {
            const radius = Math.max(1, ...parts.map(part => part.marker
                ? Math.hypot(
                    part.marker.width * Math.max(part.marker.anchor[0], 1 - part.marker.anchor[0]),
                    part.marker.height * Math.max(part.marker.anchor[1], 1 - part.marker.anchor[1])
                ) * part.marker.scale
                : 0));
            const markup = parts.map(part => {
                const marker = part.marker;
                if (!marker) return '';
                return `<g transform="translate(${radius} ${radius}) rotate(${marker.rotation * 180 / Math.PI}) scale(${marker.scale}) translate(${-marker.anchor[0] * marker.width} ${-marker.anchor[1] * marker.height})">${marker.markup}</g>`;
            }).join('');
            return { geometry: 'point', url: 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(
                `<svg xmlns="http://www.w3.org/2000/svg" width="${radius * 2}" height="${radius * 2}" viewBox="0 0 ${radius * 2} ${radius * 2}">${markup}</svg>`
            ), color: null, borderColor: null, height: `${Math.min(32, Math.ceil(radius * 2))}px` };
        }
        const layers = cim.symbolLayers?.filter(layer => layer.enable !== false) || [];
        const stroke = layers.find(layer => layer.type === 'CIMSolidStroke' && (layer.width ?? 1) > 0);
        const fill = layers.find(layer => layer.type === 'CIMSolidFill');
        return { geometry: cim.type === 'CIMLineSymbol' ? 'line' : 'polygon', url: null,
            color: cim.type === 'CIMLineSymbol'
                ? stroke ? colorToRGBA(stroke.color) : 'rgba(0, 0, 0, 0)'
                : fill ? colorToRGBA(fill.color) : 'rgba(0, 0, 0, 0)',
            borderColor: stroke ? colorToRGBA(stroke.color) : null };
    } catch {
        return null;
    }
}
